/*
  Leegverkoop: staffelbalk (winkelwagen), staffelkiezer (productpagina), snelle toevoegers en wisselmodule.
  Bron van percentages, producten, verzenddrempel en verzendkosten: #lv-config (Thema-instellingen > Leegverkoop).
  De korting zelf rekent Shopify (automatische kortingen); dit script toont en helpt alleen.
*/
import Cart from "cart-store";

const readConfig = () => {
  try {
    return JSON.parse(document.getElementById("lv-config")?.textContent || "null");
  } catch (e) {
    return null;
  }
};
const CONFIG = readConfig();
if (!CONFIG) throw new Error("lv-config ontbreekt");

const TIERS = CONFIG.tiers.slice().sort((a, b) => a.qty - b.qty);
const TOP = TIERS[TIERS.length - 1].qty;
/* zelfde notatie als het thema: €12,95; ronde bedragen zonder centen (€5) */
const money = (cents, short = false) => {
  const v = Math.round(cents) / 100;
  if (short && Number.isInteger(v)) return "€" + v;
  return "€" + v.toFixed(2).replace(".", ",");
};
const rate = (count) => TIERS.filter((x) => count >= x.qty).pop()?.pct || 0;
/* Zoals Shopify in de checkout: korting per stuk, naar beneden afgerond op hele centen */
const unitAfter = (unit, count) => unit - Math.floor((unit * rate(count)) / 100 + 1e-7);
/* getoonde percentages naar beneden afgerond, zodat het voordeel nooit groter lijkt dan het is */
const shownPct = (qty) => Math.floor(TIERS.find((t) => t.qty === qty)?.pct || 0);

const publish = (name, data) => {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {
    /* meten mag de winkel nooit breken */
  }
};

/*
  Meten zonder extra pixel: een verborgen winkelwagen-attribuut (begint met _, klant ziet het niet)
  komt mee in de bestelling. Zo zie je per bestelling of de snelle toevoegers of de wisselmodule
  zijn gebruikt. Bij ~3 bestellingen per dag zegt dat meer dan losse GA4-events.
*/
const bump = async (key) => {
  try {
    const attrs = Cart.getState()?.cart?.attributes || {};
    const n = (parseInt(attrs[key], 10) || 0) + 1;
    await fetch((window.Shopify?.routes?.root || "/") + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { [key]: String(n) } }),
    });
  } catch (e) {
    /* meten mag de winkel nooit breken */
  }
};

const openCart = () => {
  const cart = document.querySelector(".modal--cart, .popup--cart-notification");
  if (cart && !cart.shown && typeof cart.open === "function") cart.open();
};

const addToCart = async (items, button, eventName, attrKey) => {
  const label = button?.querySelector("[data-lv-add-label]");
  const original = label ? label.textContent : null;
  if (button) {
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    if (label) label.textContent = "Toevoegen…";
  }
  try {
    await Cart.add(items);
    publish(eventName, { items });
    if (attrKey) bump(attrKey);
    if (label) label.textContent = "✓ Toegevoegd";
    if (!button || !button.closest(".modal--cart")) openCart();
  } catch (e) {
    if (label) label.textContent = "Niet gelukt, opnieuw";
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute("aria-busy");
      if (label) setTimeout(() => (label.textContent = original), 2500);
    }
  }
};

/* Stuks uit de leegverkoop in de winkelwagen */
const lvLines = (cart) => (cart?.items || []).filter((item) => CONFIG.handles.includes(item.handle));
const lvCount = (cart) => lvLines(cart).reduce((s, i) => s + i.quantity, 0);
/* Totaal van de actieregels bij een bepaald aantal stuks; regels = [{ unit, qty }] */
const lvTotal = (lines, count) => lines.reduce((s, l) => s + l.qty * unitAfter(l.unit, count), 0);
/* Gratis bij het bedrag (verzendtarief) of bij het aantal stuks (automatische korting in Shopify) */
const isFree = (total, items) =>
  (CONFIG.freeShipping > 0 && total >= CONFIG.freeShipping) || (CONFIG.freeShipQty > 0 && items >= CONFIG.freeShipQty);
const shipCost = (total, items) => (CONFIG.freeShipping > 0 && !isFree(total, items) ? CONFIG.shippingCost || 0 : 0);

/* Wat verandert er als de klant één stuk van dit product toevoegt? */
const whatIf = (cart, variantId, price) => {
  const lines = lvLines(cart);
  const rows = lines.map((i) => ({ unit: i.original_price, qty: i.quantity }));
  const count = lvCount(cart);
  const now = lvTotal(rows, count);
  const next = rows.map((r) => ({ ...r }));
  const idx = lines.findIndex((i) => i.variant_id === variantId);
  if (idx > -1) next[idx].qty += 1;
  else next.push({ unit: price, qty: 1 });
  const after = lvTotal(next, count + 1);
  const delta = after - now;
  const totalAfter = cart.total_price + delta;
  const shipNow = shipCost(cart.total_price, cart.item_count);
  const shipAfter = shipCost(totalAfter, cart.item_count + 1);
  return {
    delta,
    totalAfter,
    unlocksShipping: shipNow > 0 && shipAfter === 0,
    netCost: delta + shipAfter - shipNow,
  };
};

let lastTier = null;

class LeegverkoopMeter extends HTMLElement {
  connectedCallback() {
    this.dealEl = this.querySelector("[data-lv-deal]");
    this.textEl = this.querySelector("[data-lv-text]");
    this.savingEl = this.querySelector("[data-lv-saving]");
    this.shipEl = this.querySelector("[data-lv-ship]");
    this.fillWrap = null;
    this.steps = Array.from(this.querySelectorAll("[data-lv-step]"));
    const state = Cart.getState();
    if (state?.cart) this.update(state.cart);
    this.unsubscribe = Cart.subscribe((s) => this.update(s.cart));
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  /* Vrijgespeelde verzending noemen we in dezelfde regel als de volgende trap, niet in een tweede teller */
  shipWithNext(cart, left) {
    return (
      !isFree(cart.total_price, cart.item_count) &&
      CONFIG.freeShipQty > 0 &&
      cart.item_count + left >= CONFIG.freeShipQty
    );
  }

  dealText(cart, count) {
    if (count >= TOP) {
      return `<strong>✓ ${shownPct(TOP)}% korting op je mix</strong> <small>Op alles uit de leegverkoop</small>`;
    }
    const next = TIERS.find((t) => count < t.qty);
    const now = TIERS.filter((t) => count >= t.qty).pop();
    const done = now ? `✓ ${shownPct(now.qty)}% · ` : "";
    const left = next.qty - count;
    const ship = this.shipWithNext(cart, left) ? " + gratis verzending" : "";
    return `${done}<strong>Nog ${left}</strong> voor ${shownPct(next.qty)}% korting${ship}`;
  }

  update(cart) {
    if (!cart) return;
    const count = lvCount(cart);

    if (this.dealEl) this.dealEl.hidden = count === 0;
    if (this.textEl && count > 0) this.textEl.innerHTML = this.dealText(cart, count);
    this.steps.forEach((step) => {
      const qty = Number(step.dataset.lvStep);
      step.classList.toggle("is-reached", count >= qty);
      step.classList.toggle("is-next", count > 0 && count + 1 === qty);
    });

    if (this.shipEl && CONFIG.freeShipping > 0) {
      const free = isFree(cart.total_price, cart.item_count);
      const next = TIERS.find((t) => count < t.qty);
      let html = "";
      if (free) {
        html = "✓ <strong>Gratis</strong> verzending";
      } else if (count > 0 && CONFIG.freeShipQty > 0) {
        /* staat al in de staffelregel, of nog ver weg: één vaste regel */
        html = next && this.shipWithNext(cart, next.qty - count) ? "" : `Gratis verzending vanaf ${CONFIG.freeShipQty} stuks`;
      } else {
        html = `Nog <strong>${money(CONFIG.freeShipping - cart.total_price)}</strong> tot gratis verzending`;
      }
      this.shipEl.innerHTML = html;
      this.shipEl.hidden = html === "";
      this.shipEl.classList.toggle("is-done", free);
    }

    if (this.savingEl) {
      const saving = cart.total_discount || 0;
      this.savingEl.hidden = saving <= 0;
      this.savingEl.textContent = saving > 0 ? `Je bespaart ${money(saving)}` : "";
    }

    this.updateFill(cart, count);

    if (this.hasAttribute("data-cart")) {
      const tier = TIERS.filter((t) => count >= t.qty).pop()?.qty || 0;
      if (lastTier !== null && tier > lastTier) publish("lv_tier_reached", { tier, count });
      lastTier = tier;
    }
  }

  /* Snelle toevoegers in de lade: kop, volgorde en verschil per product */
  /* In de lade staan de toevoegers los onder de producten, niet in de balk zelf */
  fillTarget() {
    if (this.fillWrap?.isConnected) return this.fillWrap;
    this.fillWrap =
      this.querySelector("[data-lv-fill]") ||
      this.closest("cart-full, .cart__wrapper, .modal--cart")?.querySelector("[data-lv-fill]") ||
      null;
    return this.fillWrap;
  }

  updateFill(cart, count) {
    if (!this.fillTarget()) return;
    const heading = this.fillWrap.querySelector("[data-lv-fill-heading]");
    const list = this.fillWrap.querySelector(".lv-fill__list");
    const items = Array.from(this.fillWrap.querySelectorAll("[data-lv-item]"));
    /* Slider (lade en winkelwagenpagina): altijd alle actieproducten, voordeligste vooraan */
    const slider = this.fillWrap.dataset.lvMode === "slider";
    if (!list || !items.length || (count === 0 && !slider)) {
      this.fillWrap.hidden = true;
      return;
    }
    if (slider && count === 0) {
      /* Nog niets uit de actie in de winkelwagen: toon de prijs vanaf 4, geen meerprijs */
      items.forEach((el) => {
        el.hidden = false;
        const unit = el.querySelector("[data-lv-unit]");
        const pill = el.querySelector("[data-lv-pill]");
        if (unit) unit.hidden = false;
        if (pill) pill.hidden = true;
      });
      this.fillWrap.hidden = false;
      return;
    }
    const freeReached = shipCost(cart.total_price, cart.item_count) === 0;
    if (count >= TOP && freeReached && !slider) {
      this.fillWrap.hidden = true;
      return;
    }

    const scored = items.map((el) => {
      const r = whatIf(cart, Number(el.dataset.lvItem), Number(el.dataset.price));
      return { el, ...r };
    });

    let show;
    if (slider) {
      show = scored.sort((a, b) => a.netCost - b.netCost);
      if (heading) heading.textContent = "Maak je routine compleet";
    } else if (count >= TOP) {
      /* Korting is binnen; alleen nog verzending: het goedkoopste product dat de drempel haalt */
      show = scored.filter((s) => s.unlocksShipping).sort((a, b) => a.delta - b.delta).slice(0, 1);
      if (heading) heading.textContent = "Haal je gratis verzending";
    } else {
      show = scored.sort((a, b) => a.netCost - b.netCost).slice(0, 2);
      if (heading) heading.textContent = "Maak je routine compleet";
    }
    if (!show.length) {
      this.fillWrap.hidden = true;
      return;
    }

    items.forEach((el) => (el.hidden = true));
    show.forEach((s) => {
      s.el.hidden = false;
      list.appendChild(s.el);
      const pill = s.el.querySelector("[data-lv-pill]");
      const unit = s.el.querySelector("[data-lv-unit]");
      if (unit) unit.hidden = true;
      if (pill) {
        /* Bij dure producten (sweater, sportfles) maakt een extra stuk het totaal soms lager */
        const amount =
          s.delta < 0 ? `−${money(-s.delta, true)} op je totaal` : s.delta === 0 ? "Gratis" : `+${money(s.delta, true)}`;
        pill.textContent = `${amount}${s.unlocksShipping ? " · gratis verzending" : ""}`;
        pill.classList.add("lv-chip--delta");
        pill.hidden = false;
      }
    });
    this.fillWrap.hidden = false;
  }
}

/* Staffelkiezer op de productpagina: tegels en teller zetten het aantal; de koopknop toont het bedrag.
   Aantallen zonder eigen tegel (3, 5) zetten geen tegel aan. */
class LvStaffel extends HTMLElement {
  connectedCallback() {
    const root = this.closest(".shopify-section") || document;
    this.form = root.querySelector("form.js-product-form");
    this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
    /* De teller voor 5+ staat onder de koopknop (los blok), daarom zoeken in de hele sectie */
    this.more = root.querySelector("[data-lv-more]");
    this.qtyInput = this.more?.querySelector("[data-lv-qty]");
    this.price = Number(this.dataset.price || 0);
    if (!this.form || !this.radios.length) return;

    this.target =
      root.querySelector(`input[name="quantity"][form="${this.form.id}"]`) ||
      this.form.querySelector('input[name="quantity"]');
    if (!this.target) {
      this.target = document.createElement("input");
      this.target.type = "hidden";
      this.target.name = "quantity";
      this.target.value = "1";
      this.form.appendChild(this.target);
    }
    this.buttonText = this.form.querySelector(".js-product-add .staged-action__text");
    this.buttonLabel = this.buttonText ? this.buttonText.textContent.trim() : null;

    this.radios.forEach((r) => r.addEventListener("change", () => this.set(Number(r.value))));
    this.more?.querySelectorAll("[data-lv-step]").forEach((b) =>
      b.addEventListener("click", () => this.set(Math.max(1, this.value() + Number(b.dataset.lvStep))))
    );
    this.qtyInput?.addEventListener("change", () => this.set(Math.max(1, parseInt(this.qtyInput.value, 10) || 1)));

    /* Na herladen of terugknop kan de browser een andere tegel aangevinkt laten: neem die over */
    const checked = this.radios.find((r) => r.checked);
    this.set(checked ? Number(checked.value) : 1);
  }

  value() {
    return parseInt(this.target.value, 10) || 1;
  }

  total(qty) {
    const tile = this.radios.find((r) => Number(r.value) === qty);
    if (tile?.dataset.total) return Number(tile.dataset.total);
    return qty * unitAfter(this.price, qty);
  }

  set(qty) {
    this.target.value = String(qty);
    this.target.dispatchEvent(new Event("change", { bubbles: true }));
    if (this.qtyInput) this.qtyInput.value = String(qty);
    /* Tegel aan bij exact dat aantal; boven de grootste tegel blijft de grootste aan */
    const max = Math.max(...this.radios.map((r) => Number(r.value)));
    this.radios.forEach((r) => {
      r.checked = Number(r.value) === Math.min(qty, max);
      r.closest(".lv-tile")?.classList.toggle("is-checked", r.checked);
    });
    if (this.buttonText && this.buttonLabel && this.price) {
      this.buttonText.textContent = `${this.buttonLabel} · ${money(this.total(qty), true)}`;
    }
  }
}

/* Snelle toevoegers: één tik voegt toe */
class LvFill extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-lv-add]");
      if (!btn) return;
      e.preventDefault();
      addToCart([{ id: Number(btn.dataset.lvAdd), quantity: 1 }], btn, "lv_fill4_add", "_lv_snel_toegevoegd");
    });
  }
}

/* Wisselmodule op bundelpagina's: losse stuks in de winkelwagen */
class LvSwitch extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector("[data-lv-switch]");
    const items = (this.dataset.items || "")
      .split(",")
      .filter(Boolean)
      .map((pair) => {
        const [id, quantity] = pair.split(":");
        return { id: Number(id), quantity: Number(quantity) };
      });
    btn?.addEventListener("click", () => addToCart(items, btn, "lv_switch_click", "_lv_los_ipv_bundel"));
  }
}

const define = (name, cls) => {
  if (!customElements.get(name)) customElements.define(name, cls);
};
define("leegverkoop-meter", LeegverkoopMeter);
define("lv-staffel", LvStaffel);
define("lv-fill", LvFill);
define("lv-switch", LvSwitch);
